import { useEffect, useState } from "react";
import {
    doc,
    deleteDoc,
    setDoc,
    serverTimestamp,
    updateDoc,
    onSnapshot,
    collection,
    query,
    where,
    orderBy,
} from "firebase/firestore";
import { db } from "../firebase";
import RealtimeWaitingTime from "./RealtimeWaitingTime";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";

const JoinerStatus = ({
    pendingOrder,
    clubId,
    sessionId,
    hasAcknowledged,
    setHasAcknowledged,
    player,
    setPlayer,
    queue,
    setQueue,
    activeMatch,
    setActiveMatch,
    gamesPlayed,
    setGamesPlayed,
    user,
    THEME_VIOLET,
    stopAlert2,
}) => {
    const navigate = useNavigate();
    const uid = user?.uid;

    const leaveQueue = async () => {
        const result = await Swal.fire({
            title: "Leave queue?",
            html: `<p>You've played <strong>${gamesPlayed}</strong> games today.</p>`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, leave",
            confirmButtonColor: "#dc3545",
            reverseButtons: true,
        });

        if (!result.isConfirmed) return;

        await deleteDoc(doc(db, "clubs", clubId, "sessions", sessionId, "queue", uid));
        await updateDoc(
            doc(db, "clubs", clubId, "sessions", sessionId, "players", uid),
            { status: "left", leftAt: serverTimestamp() }
        );
    };

    const rejoinQueue = async () => {
        await setDoc(
            doc(db, "clubs", clubId, "sessions", sessionId, "queue", uid),
            {
                playerId: uid,
                photoURL: user.photoURL,
                username: user.username,
                name: player.name,
                level: player.level,
                waitingSince: player.waitingSince,
                joinedAt: serverTimestamp(),
            },
            { merge: true }
        );

        await updateDoc(
            doc(db, "clubs", clubId, "sessions", sessionId, "players", uid),
            { status: "waiting" }
        );
    };

    if (!player) return null;

    const index = queue.findIndex((p) => p.playerId === uid);
    const position = index >= 0 ? index + 1 : null;

    return (
        <div className="mt-4 px-2">

            {/* 1. FULLSCREEN ALERT */}
            {player.status === "playing" && activeMatch && !hasAcknowledged && (
                <div
                    style={{
                        position: "fixed",
                        inset: 0,
                        background: `linear-gradient(135deg, ${THEME_VIOLET} 0%, #1a052c 100%)`,
                        zIndex: 9999,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        color: "#fff",
                        textAlign: "center",
                        padding: "20px",
                    }}
                >
                    <span style={{ fontSize: "5rem" }}>🏸</span>

                    <h1 className="fw-black" style={{ fontSize: "3.5rem" }}>
                        IT'S TIME!
                    </h1>

                    <div
                        className="my-4 p-4 rounded-circle border border-4 shadow-lg"
                        style={{
                            width: "200px",
                            height: "200px",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            background: "rgba(255,255,255,0.1)",
                            borderColor: "rgba(255,255,255,0.4)",
                        }}
                    >
                        <span className="small opacity-75 fw-bold">COURT</span>
                        <span className="display-1 fw-black">
                            {activeMatch.court}
                        </span>
                    </div>

                    <p className="fs-5 text-warning fw-bold mb-4">
                        Proceed to the court immediately
                    </p>

                    <button
                        className="btn btn-light btn-lg rounded-pill px-5 fw-bold shadow"
                        onClick={() => {
                            if (navigator.vibrate) navigator.vibrate([100, 50, 100]);
                            stopAlert2();
                            setHasAcknowledged(true);
                        }}
                        style={{ color: THEME_VIOLET }}
                    >
                        I'M ON MY WAY
                    </button>
                </div>
            )}

            {/* 2. ACTIVE MATCH DASHBOARD */}
            {player.status === "playing" && activeMatch && hasAcknowledged && (
                <div>
                    <div
                        className="card border-0 shadow-lg rounded-4 overflow-hidden mb-4"
                        style={{ background: "var(--card-bg)" }}
                    >
                        <div
                            className="p-3 text-white d-flex justify-content-between align-items-center"
                            style={{ backgroundColor: "#198754" }}
                        >
                            <span className="fw-bold small text-uppercase">
                                LIVE MATCH
                            </span>
                            <span
                                className="badge rounded-pill px-3"
                                style={{
                                    background: "var(--surface-bg)",
                                    color: "var(--text-color)",
                                }}
                            >
                                IN PROGRESS
                            </span>
                        </div>

                        <div className="card-body p-4 text-center">
                            <h2
                                className="display-4 fw-bold mb-3"
                                style={{ color: "#198754" }}
                            >
                                COURT {activeMatch.court}
                            </h2>

                            <div
                                className="row g-0 py-3 border-top border-bottom my-3 rounded-3 align-items-center"
                                style={{
                                    background: "var(--surface-bg)",
                                    borderColor: "var(--border-color)",
                                }}
                            >
                                <div className="col-5 fw-bold small">
                                    {activeMatch.players?.[0]?.name}
                                    <br />
                                    {activeMatch.players?.[1]?.name}
                                </div>

                                <div className="col-2 text-center">
                                    <span
                                        className="badge rounded-circle p-2"
                                        style={{
                                            background: "var(--border-color)",
                                            color: "var(--text-color)",
                                        }}
                                    >
                                        VS
                                    </span>
                                </div>

                                <div className="col-5 fw-bold small">
                                    {activeMatch.players?.[2]?.name}
                                    <br />
                                    {activeMatch.players?.[3]?.name}
                                </div>
                            </div>

                            <p
                                className="small mb-0"
                                style={{ color: "var(--muted-text)" }}
                            >
                                Good luck! Remember to clear the court on time.
                            </p>
                        </div>

                        <div
                            className="card-footer border-0 p-3 pt-0"
                            style={{ background: "var(--card-bg)" }}
                        >
                            <button
                                className="btn btn-outline-secondary w-100 rounded-pill btn-sm"
                                onClick={() =>
                                    Swal.fire(
                                        "Court Issue?",
                                        "If the court is occupied or needs cleaning, please alert the coordinator.",
                                        "info"
                                    )
                                }
                            >
                                Report Court Issue
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* 3. STANDARD HUB */}
            {player.status !== "playing" && (
                <>
                    <div
                        className="card border-0 shadow-lg rounded-4 overflow-hidden mb-4"
                        style={{ background: "var(--card-bg)" }}
                    >
                        <div
                            className="p-3 text-white d-flex justify-content-between align-items-center"
                            style={{ backgroundColor: THEME_VIOLET }}
                        >
                            <span className="fw-bold small text-uppercase">
                                Your Session
                            </span>
                            <span
                                className="badge rounded-pill small px-3"
                                style={{
                                    background: "var(--surface-bg)",
                                    color: "var(--text-color)",
                                }}
                            >
                                {player.status?.toUpperCase()}
                            </span>
                        </div>

                        <div className="card-body p-4">
                            <div className="row g-3 text-center">
                                <div className="col-4 border-end" style={{ borderColor: "var(--border-color)" }}>
                                    <div className="fs-4 fw-bold" style={{ color: THEME_VIOLET }}>
                                        {position ? `#${position}` : "--"}
                                    </div>
                                    <div className="small" style={{ color: "var(--muted-text)" }}>
                                        Position
                                    </div>
                                </div>

                                <div className="col-4 border-end" style={{ borderColor: "var(--border-color)" }}>
                                    <div className="fs-4 fw-bold" style={{ color: "var(--text-color)" }}>
                                        {player.status === "waiting" && player.waitingSince ? (
                                            <RealtimeWaitingTime waitingSince={player.waitingSince} />
                                        ) : (
                                            "00:00"
                                        )}
                                    </div>
                                    <div className="small" style={{ color: "var(--muted-text)" }}>
                                        Waiting
                                    </div>
                                </div>

                                <div className="col-4">
                                    <div className="fs-4 fw-bold" style={{ color: "var(--text-color)" }}>
                                        {gamesPlayed}
                                    </div>
                                    <div className="small" style={{ color: "var(--muted-text)" }}>
                                        Games
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* QUEUE SECTION */}
                    <div className="mb-3">
                        <h6 className="fw-bold mb-3 d-flex justify-content-between align-items-center">
                            <span>Current Queue</span>
                            <span
                                className="badge fw-normal"
                                style={{
                                    background: "var(--surface-bg)",
                                    color: "var(--muted-text)",
                                }}
                            >
                                {queue.length} players
                            </span>
                        </h6>

                        <div
                            className="rounded-4 overflow-hidden border"
                            style={{ borderColor: "var(--border-color)" }}
                        >
                            <div
                                style={{
                                    maxHeight: "350px",
                                    overflowY: "auto",
                                    background: "var(--surface-bg)",
                                }}
                            >
                                <ul className="list-group list-group-flush">
                                    {queue.map((p, i) => {
                                        const isMe = p.playerId === uid;
                                        const hasProfile = !!p.username;

                                        return (
                                            <li
                                                key={p.playerId}
                                                onClick={() => {
                                                    if (!hasProfile) return;
                                                    navigate(
                                                        isMe
                                                            ? `/me`
                                                            : `/u/${p.username}?club=${clubId}&session=${sessionId}`,
                                                        {
                                                            state: { from: "join", clubId, sessionId },
                                                        }
                                                    );
                                                }}
                                                className="list-group-item d-flex justify-content-between align-items-center py-3 mb-2 border-0"
                                                style={{
                                                    borderRadius: "16px",
                                                    background: isMe
                                                        ? "var(--card-bg)"
                                                        : "color-mix(in srgb, var(--card-bg) 80%, transparent)",
                                                    borderLeft: isMe
                                                        ? `5px solid ${THEME_VIOLET}`
                                                        : "5px solid transparent",
                                                }}
                                            >
                                                <div className="d-flex align-items-center">
                                                    <span
                                                        className="me-3 small fw-bold"
                                                        style={{ width: "20px", color: "var(--muted-text)" }}
                                                    >
                                                        {i + 1}
                                                    </span>

                                                    {/* Profile Picture / Avatar */}
                                                    <div className="position-relative me-3">
                                                        {p.photoURL ? (
                                                            <img
                                                                src={p.photoURL}
                                                                alt={p.name}
                                                                className="rounded-circle border border-2"
                                                                style={{
                                                                    width: '45px',
                                                                    height: '45px',
                                                                    objectFit: 'cover',
                                                                    borderColor: isMe ? THEME_VIOLET : '#eee'
                                                                }}
                                                            />
                                                        ) : (
                                                            <div
                                                                className="rounded-circle d-flex align-items-center justify-content-center fw-bold text-white"
                                                                style={{
                                                                    width: "45px",
                                                                    height: "45px",
                                                                    backgroundColor: isMe
                                                                        ? THEME_VIOLET
                                                                        : "var(--border-color)",
                                                                }}
                                                            >
                                                                {p.name.charAt(0).toUpperCase()}
                                                            </div>
                                                        )}
                                                        {/* Online Status Dot (Optional) */}
                                                        <span className="position-absolute bottom-0 end-0 p-1 bg-success border border-white rounded-circle" style={{ width: '12px', height: '12px' }}></span>
                                                    </div>

                                                    <div>
                                                        <div
                                                            className="fw-bold"
                                                            style={{
                                                                color: isMe
                                                                    ? THEME_VIOLET
                                                                    : "var(--text-color)",
                                                            }}
                                                        >
                                                            {p.name}
                                                            {isMe && <small className="ms-1">(You)</small>}
                                                            {!hasProfile && (
                                                                <small className="ms-2" style={{ color: "var(--muted-text)" }}>
                                                                    (No profile)
                                                                </small>
                                                            )}
                                                        </div>

                                                        <span
                                                            className="badge border"
                                                            style={{
                                                                background: "var(--surface-bg)",
                                                                color: "var(--muted-text)",
                                                                borderColor: "var(--border-color)",
                                                                fontSize: "0.65rem",
                                                            }}
                                                        >
                                                            LVL {p.level}
                                                        </span>
                                                    </div>
                                                </div>

                                                {isMe ? (
                                                    <span
                                                        className="badge rounded-pill px-3 py-2 text-white"
                                                        style={{ backgroundColor: THEME_VIOLET }}
                                                    >
                                                        IN QUEUE
                                                    </span>
                                                ) : (
                                                    <i className="bi bi-chevron-right" style={{ color: "var(--muted-text)" }}></i>
                                                )}
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        </div>
                    </div>
                </>
            )}
            {/* 4. STICKY ACTION BAR (Hidden while Playing) */}
            {!["playing", "pending", "removed"].includes(player.status) && (
                <div
                    className="fixed-bottom border-top p-3 shadow"
                    style={{
                        bottom: 56, // 👈 sits ABOVE BottomNav
                        zIndex: 1020,
                        background: "var(--card-bg)",
                        borderColor: "var(--border-color)",
                    }}
                >
                    <div className="container" style={{ maxWidth: 420 }}>
                        {(player?.status === "left" || player?.status === "removed") ? (
                            <button
                                className="btn w-100 rounded-pill fw-bold py-2 text-white"
                                style={{ backgroundColor: THEME_VIOLET }}
                                onClick={rejoinQueue}
                            >
                                <i className="bi bi-arrow-repeat me-2"></i>
                                Rejoin Queue
                            </button>
                        ) : (
                            <button
                                className="btn btn-outline-danger w-100 rounded-pill fw-bold py-2"
                                onClick={leaveQueue}
                            >
                                <i className="bi bi-door-open me-2"></i>
                                Leave Queue
                            </button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default JoinerStatus;